import { useEffect, useMemo } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType, object, ObjectSchema, string } from 'yup';

import {
  Button,
  FormField,
  FormSubmitButton,
  GridCol,
  GridRow,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';
import { usePutPersonsCompanyUserInviteMutation } from '@client/api';
import { toast } from 'react-hot-toast';

type FormModel = {
  email: string;
};

const schema: ObjectSchema<FormModel> = object({
  email: string().email().required(),
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormModel>;
type FormDefaultValues = DeepPartial<FormValues>;

export type UserInviteFormData = FormValues;

interface CompanyUserInviteModalProps {
  companyId?: string;
  open: boolean;
  onClose: () => void;
  onSubmited: () => void;
}
export function CompanyUserInviteModal({ companyId, open, onClose, onSubmited }: CompanyUserInviteModalProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const [putPersonsUserInvite, { isLoading }] = usePutPersonsCompanyUserInviteMutation();
  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      email: '',
    }),
    [],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { handleSubmit, reset } = methods;

  const handleFormSubmit = async (data: FormValues) => {
    await putPersonsUserInvite({ body: { email: data.email, companyId } })
      .unwrap()
      .then(() => {
        toast.success(tGlobal('user.userInvitedSuccessfully'));
        onSubmited();
      })
      .catch((error) => {});
  };

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, open]);

  return (
    <Modal open={open} size="lg" onClose={onClose} title={tCommon('inviteUsertoCompany')}>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalContent className="space-y-8 p-4">
            <GridRow cols={{ def: 1, sm: 6 }} spacingX={2} spacingY={4}>
              <GridCol span={{ def: 1, sm: 6 }}>
                <FormField<FormValues, FormPath>
                  name="email"
                  label={tGlobal('user.email')}
                  required
                  render={({ field }) => <Input {...field} />}
                />
              </GridCol>
            </GridRow>
          </ModalContent>
          <ModalFooter>
            <Button color="primary-dark" onClick={onClose}>
              {tCommon('back')}
            </Button>
            <FormSubmitButton type="submit" progressing={isLoading}>
              {tCommon('send')}
            </FormSubmitButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
}
